import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  Alert,
  CircularProgress,
  Snackbar,
  TextField,
  Slider,
  Grid,
  Paper,
  Stack,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import AcUnitIcon from "@mui/icons-material/AcUnit";
import PauseIcon from "@mui/icons-material/Pause";
import BlockIcon from "@mui/icons-material/Block";
import type { ArbitrageOpportunity } from "../../types/opportunity";
import {
  getExchangeName,
  getQuoteCurrency,
  getQuoteCurrencySymbol,
  getQuoteCurrencyColor,
  makeOpportunityKey,
} from "../../types/opportunity";
import { useSettings } from "../../hooks/useSettings";
import { useMarkPrice } from "../../hooks/useMarkPrice";
import { usePolledOrderbook } from "../../hooks/usePolledOrderbook";
import { usePriceUpdates } from "../../hooks/usePriceUpdates";
import {
  useOpportunityLiveUpdate,
  formatTimeSinceUpdate,
} from "../../hooks/useOpportunityLiveUpdate";
import {
  getAvailableStrategies,
  type StrategyType,
} from "../../utils/strategyHelpers";
import {
  executeStrategy,
  getWithdrawalChance,
  type PriceLevel,
  type ProfitTier,
  type WithdrawalChanceResponse,
} from "../../api/strategies";
import { formatPrice } from "../../utils/formatters";
import { getExchangeLogo } from "../../utils/exchangeUrls";
import { Avatar } from "@mui/material";
import { ProfitBreakdownDisplay } from "./ProfitBreakdownDisplay";
import { useTranslation } from "react-i18next";
import type { OrderbookPriceLevel } from "../../types/orderbook";

// Signed percent fallback: must match the negative DEFAULT_SETTINGS convention.
// A positive value here would submit a positive ratio and arm immediate liquidation.
const DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT = -3;

/**
 * Map exchange index to CCXT exchange name
 */
function getExchangeNameForCcxt(exchangeIndex: number): string {
  switch (exchangeIndex) {
    case 0:
      return "upbit";
    case 1:
      return "bithumb";
    case 3:
      return "binance";
    default:
      return "binance";
  }
}

/**
 * Calculate which levels are covered by budget and return average price
 */
function calculateBudgetCoverage(
  levels: OrderbookPriceLevel[],
  budgetInQuote: number,
): { highlightCount: number; avgPrice: number | null; totalVolume: number } {
  if (levels.length === 0 || budgetInQuote <= 0) {
    return { highlightCount: 0, avgPrice: null, totalVolume: 0 };
  }

  let remainingBudget = budgetInQuote;
  let totalCost = 0;
  let totalVolume = 0;
  let highlightCount = 0;

  for (const level of levels) {
    const levelCost = level.price * level.volume;

    if (remainingBudget <= 0) break;

    if (levelCost <= remainingBudget) {
      totalCost += levelCost;
      totalVolume += level.volume;
      remainingBudget -= levelCost;
      highlightCount++;
    } else {
      const partialVolume = remainingBudget / level.price;
      totalCost += remainingBudget;
      totalVolume += partialVolume;
      highlightCount++;
      break;
    }
  }

  const avgPrice = totalVolume > 0 ? totalCost / totalVolume : null;
  return { highlightCount, avgPrice, totalVolume };
}

/**
 * Compact Orderbook Card Component
 */
interface OrderbookCardProps {
  title: string;
  exchangeIndex: number;
  levels: OrderbookPriceLevel[];
  loading: boolean;
  error: string | null;
  quoteCurrency: string;
  avgPrice: number | null;
  highlightCount: number;
  isBid: boolean; // true for bids (sell side), false for asks (buy side)
  quoteToUsdtRate?: number | null;
}

const OrderbookCard: React.FC<OrderbookCardProps> = ({
  title,
  exchangeIndex,
  levels,
  loading,
  error,
  quoteCurrency,
  avgPrice,
  highlightCount,
  isBid,
  quoteToUsdtRate,
}) => {
  const hasData = levels.length > 0;
  const isNonUsdt = quoteCurrency !== "U" && quoteCurrency !== "USDT";

  // Sort levels appropriately
  const sortedLevels = isBid
    ? [...levels].sort((a, b) => b.price - a.price) // Bids: highest first
    : [...levels].sort((a, b) => a.price - b.price); // Asks: lowest first

  const displayLevels = isBid
    ? sortedLevels.slice(0, Math.max(5, highlightCount))
    : sortedLevels.slice(0, Math.max(5, highlightCount)).reverse();

  // Helper to convert price to USDT
  const toUsdt = (price: number): number | null => {
    if (!quoteToUsdtRate) return null;
    if (quoteCurrency === "K" || quoteCurrency === "KRW") {
      return price / quoteToUsdtRate;
    }
    if (quoteCurrency === "B" || quoteCurrency === "BTC") {
      return price * quoteToUsdtRate;
    }
    return price;
  };

  const formatUsdtEquiv = (usdtPrice: number): string => {
    let decimals: number;
    if (usdtPrice >= 1000) decimals = 2;
    else if (usdtPrice >= 1) decimals = 4;
    else if (usdtPrice >= 0.0001) decimals = 6;
    else decimals = 8;
    return `$${usdtPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: decimals })}`;
  };

  return (
    <Paper
      elevation={2}
      sx={{
        p: 1.5,
        borderRadius: 1,
        background: isBid
          ? "linear-gradient(135deg, rgba(76,175,80,0.1) 0%, rgba(76,175,80,0.05) 100%)"
          : "linear-gradient(135deg, rgba(244,67,54,0.1) 0%, rgba(244,67,54,0.05) 100%)",
        border: `1px solid ${isBid ? "rgba(76,175,80,0.3)" : "rgba(244,67,54,0.3)"}`,
        minHeight: 200,
      }}
    >
      <Stack spacing={1}>
        {/* Header with exchange logo and title */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Avatar
            src={getExchangeLogo(exchangeIndex)}
            sx={{ width: 24, height: 24 }}
          />
          <Typography
            variant="caption"
            fontWeight="bold"
            color="text.secondary"
          >
            {title}
          </Typography>
        </Stack>

        {/* Loading State */}
        {loading && !hasData && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}

        {/* Error State */}
        {error && !hasData && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <Tooltip title={error}>
              <BlockIcon sx={{ fontSize: 32, color: "text.disabled" }} />
            </Tooltip>
          </Box>
        )}

        {/* Orderbook Data */}
        {hasData && (
          <>
            {/* Average Price */}
            <Box>
              <Typography variant="caption" color="text.secondary">
                {isBid ? "Avg Sell" : "Avg Buy"}
              </Typography>
              <Stack direction="row" alignItems="baseline" spacing={0.5}>
                <Typography variant="body1" fontWeight="bold">
                  {avgPrice !== null
                    ? formatPrice(avgPrice, quoteCurrency)
                    : "—"}
                </Typography>
                {isNonUsdt &&
                  avgPrice !== null &&
                  toUsdt(avgPrice) !== null && (
                    <Typography variant="caption" color="text.secondary">
                      ({formatUsdtEquiv(toUsdt(avgPrice)!)})
                    </Typography>
                  )}
              </Stack>
            </Box>

            {/* Price Levels */}
            <Stack spacing={0.25} sx={{ maxHeight: 120, overflow: "auto" }}>
              {displayLevels.map((level, idx) => {
                const sortedIdx = isBid ? idx : displayLevels.length - 1 - idx;
                const isHighlighted = sortedIdx < highlightCount;
                return (
                  <Stack
                    key={idx}
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      px: 0.5,
                      py: 0.25,
                      borderRadius: 0.25,
                      background: isHighlighted
                        ? isBid
                          ? "rgba(0,255,0,0.15)"
                          : "rgba(255,0,0,0.15)"
                        : "transparent",
                      fontSize: "0.7rem",
                    }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={isHighlighted ? 600 : 400}
                    >
                      {formatPrice(level.price, quoteCurrency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {level.volume.toFixed(4)}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </>
        )}
      </Stack>
    </Paper>
  );
};

interface StrategyExecutionDialogProps {
  open: boolean;
  onClose: () => void;
  opportunity: ArbitrageOpportunity;
  traderName: string;
}

export const StrategyExecutionDialog: React.FC<
  StrategyExecutionDialogProps
> = ({ open, onClose, opportunity: initialOpportunity, traderName }) => {
  const { t } = useTranslation();
  const { settings, saveSettings } = useSettings();
  const [selectedStrategy, setSelectedStrategy] = useState<StrategyType | null>(
    null,
  );
  const [executing, setExecuting] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error" | "warning"
  >("success");
  const [, setTimeTicker] = useState(0);
  const [isFrozen, setIsFrozen] = useState(false);
  const [frozenOpportunity, setFrozenOpportunity] =
    useState<ArbitrageOpportunity | null>(null);

  // Adjustable parameters
  const [budget, setBudget] = useState(settings.strategyBudget);
  const [leverage, setLeverage] = useState(settings.leverage);
  const [profitTiers, setProfitTiers] = useState(settings.profitTiers);
  const [adaptiveProfitPercentage, setAdaptiveProfitPercentage] =
    useState(0.05); // Default 5%, stored as decimal
  const [spotFuturesGapMagnitude, setSpotFuturesGapMagnitude] = useState(
    (
      settings.hedgeConfig.spotFuturesGapMagnitude ??
      DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT
    ).toString(),
  );

  // Debug preview dialog
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewJson, setPreviewJson] = useState("");

  // Withdrawal chance state (for daily limit capping)
  const [withdrawalChance, setWithdrawalChance] =
    useState<WithdrawalChanceResponse | null>(null);
  const [withdrawalChanceLoading, setWithdrawalChanceLoading] = useState(false);
  const [withdrawalChanceError, setWithdrawalChanceError] = useState<
    string | null
  >(null);

  // Track if dialog has been initialized for current session
  // Prevents state reset on every live opportunity update
  const initializedForSessionRef = useRef(false);

  // Get live opportunity data
  const opportunityKey = makeOpportunityKey(initialOpportunity);
  const {
    opportunity: liveOpportunity,
    isAvailable,
    lastUpdateTime,
  } = useOpportunityLiveUpdate(opportunityKey);

  // Use frozen snapshot if frozen, otherwise use live opportunity if available, otherwise always fall back to initial
  // IMPORTANT: Always keep the dialog open even if opportunity disappears from live feed
  const opportunity =
    isFrozen && frozenOpportunity
      ? frozenOpportunity
      : liveOpportunity || initialOpportunity;

  // Check futures data availability for this symbol
  // Note: Futures orderbooks are no longer subscribed (per Epic 10)
  // Use markPriceData.bestBid/bestAsk from book ticker stream instead
  const futuresSymbol = `${opportunity.base}/USDT`;
  const markPrice = useMarkPrice(futuresSymbol);
  const hasFuturesData = markPrice !== undefined;

  // Get available strategies based on futures data
  const availableStrategies = useMemo(
    () => getAvailableStrategies(hasFuturesData),
    [hasFuturesData],
  );

  // Price data for currency conversion rates
  const priceData = usePriceUpdates();
  const krwToUsdtRate = useMemo(() => {
    if (!priceData?.upbitBtcKrw || !priceData?.binanceBtcUsdt) return null;
    return priceData.upbitBtcKrw / priceData.binanceBtcUsdt;
  }, [priceData?.upbitBtcKrw, priceData?.binanceBtcUsdt]);
  const btcUsdtRate = priceData?.binanceBtcUsdt ?? null;

  // Orderbook polling - poll only when dialog is open
  // Buy exchange orderbook (asks - where we buy)
  const buyExchangeCcxt = getExchangeNameForCcxt(
    initialOpportunity.buy_exchange,
  );
  const buyQuoteCurrency =
    initialOpportunity.buy_quote === "K"
      ? "KRW"
      : initialOpportunity.buy_quote === "B"
        ? "BTC"
        : "USDT";
  const buyCcxtSymbol = `${initialOpportunity.base}/${buyQuoteCurrency}`;
  const buyOrderbook = usePolledOrderbook({
    exchange: buyExchangeCcxt,
    symbol: buyCcxtSymbol,
    enabled: open,
    isFutures: false,
  });

  // Sell exchange orderbook (bids - where we sell)
  const sellExchangeCcxt = getExchangeNameForCcxt(
    initialOpportunity.sell_exchange,
  );
  const sellQuoteCurrency =
    initialOpportunity.sell_quote === "K"
      ? "KRW"
      : initialOpportunity.sell_quote === "B"
        ? "BTC"
        : "USDT";
  const sellCcxtSymbol = `${initialOpportunity.base}/${sellQuoteCurrency}`;
  const sellOrderbook = usePolledOrderbook({
    exchange: sellExchangeCcxt,
    symbol: sellCcxtSymbol,
    enabled: open,
    isFutures: false,
  });

  // Futures orderbook (bids - where we short/hedge) - only for hedge strategies
  const isHedgeSelected = selectedStrategy && selectedStrategy !== "Cross";
  const futuresCcxtSymbol = `${initialOpportunity.base}/USDT`;
  const futuresOrderbook = usePolledOrderbook({
    exchange: "binanceusdm",
    symbol: futuresCcxtSymbol,
    enabled: open && isHedgeSelected === true,
    isFutures: true,
  });

  // Calculate budget in quote currencies for orderbook coverage calculation
  const buyBudgetInQuote = useMemo(() => {
    if (initialOpportunity.buy_quote === "K" && krwToUsdtRate) {
      return budget * krwToUsdtRate;
    }
    if (initialOpportunity.buy_quote === "B" && btcUsdtRate) {
      return budget / btcUsdtRate;
    }
    return budget;
  }, [budget, initialOpportunity.buy_quote, krwToUsdtRate, btcUsdtRate]);

  const sellBudgetInQuote = useMemo(() => {
    if (initialOpportunity.sell_quote === "K" && krwToUsdtRate) {
      return budget * krwToUsdtRate;
    }
    if (initialOpportunity.sell_quote === "B" && btcUsdtRate) {
      return budget / btcUsdtRate;
    }
    return budget;
  }, [budget, initialOpportunity.sell_quote, krwToUsdtRate, btcUsdtRate]);

  // Calculate budget coverage for each orderbook
  const sortedBuyAsks = useMemo(
    () => [...(buyOrderbook.asks || [])].sort((a, b) => a.price - b.price),
    [buyOrderbook.asks],
  );
  const sortedSellBids = useMemo(
    () => [...(sellOrderbook.bids || [])].sort((a, b) => b.price - a.price),
    [sellOrderbook.bids],
  );
  const sortedFuturesBids = useMemo(
    () => [...(futuresOrderbook.bids || [])].sort((a, b) => b.price - a.price),
    [futuresOrderbook.bids],
  );

  const buyCoverage = useMemo(
    () => calculateBudgetCoverage(sortedBuyAsks, buyBudgetInQuote),
    [sortedBuyAsks, buyBudgetInQuote],
  );
  const sellCoverage = useMemo(
    () => calculateBudgetCoverage(sortedSellBids, sellBudgetInQuote),
    [sortedSellBids, sellBudgetInQuote],
  );
  const futuresCoverage = useMemo(
    () => calculateBudgetCoverage(sortedFuturesBids, budget),
    [sortedFuturesBids, budget],
  );

  // Get exchange names and currency symbols - moved up before dailyLimitCap calculation
  const buyExchange = getExchangeName(opportunity.buy_exchange);
  const sellExchange = getExchangeName(opportunity.sell_exchange);
  const buyQuote = getQuoteCurrency(opportunity.buy_quote);
  const sellQuote = getQuoteCurrency(opportunity.sell_quote);
  const buyQuoteSymbol = getQuoteCurrencySymbol(opportunity.buy_quote);
  const sellQuoteSymbol = getQuoteCurrencySymbol(opportunity.sell_quote);
  const buyQuoteColor = getQuoteCurrencyColor(opportunity.buy_quote);
  const sellQuoteColor = getQuoteCurrencyColor(opportunity.sell_quote);

  // Calculate spread - moved up before dailyLimitCap calculation
  const buyPrice = opportunity.buy_price || 0;
  const sellPrice = opportunity.sell_price || 0;
  const spreadAmount = sellPrice - buyPrice;
  const spreadPercent = buyPrice > 0 ? (spreadAmount / buyPrice) * 100 : 0;

  // Calculate daily limit cap for budget
  // Returns { isCapped, originalBudget, cappedBudget, dailyLimitInUSDT, remainingDailyFiat }
  const dailyLimitCap = useMemo(() => {
    if (!withdrawalChance || !withdrawalChance.withdraw_limit.daily) {
      return null;
    }

    const dailyLimit = parseFloat(withdrawalChance.withdraw_limit.daily);
    if (isNaN(dailyLimit) || dailyLimit <= 0) {
      return null;
    }

    // Calculate how much volume we'd need to withdraw based on budget
    // We buy at buyPrice, so budget / buyPrice = volume to withdraw
    const volumeNeeded = budget / buyPrice;

    // Check if daily limit would cap us
    if (dailyLimit >= volumeNeeded) {
      return null; // No cap needed
    }

    // Calculate capped budget in USDT
    // If daily limit is in crypto units, convert to budget equivalent
    const cappedBudget = dailyLimit * buyPrice;

    // Convert to USDT if buy quote is KRW
    let cappedBudgetUsdt = cappedBudget;
    if (opportunity.buy_quote === "K") {
      cappedBudgetUsdt = cappedBudget / 1535; // KRW to USDT
    }

    // Get remaining daily in fiat for display
    const remainingDailyFiat =
      parseFloat(withdrawalChance.withdraw_limit.remaining_daily_fiat) || 0;
    const fiatCurrency = withdrawalChance.withdraw_limit.fiat_currency || "KRW";

    return {
      isCapped: true,
      originalBudget: budget,
      cappedBudget: cappedBudgetUsdt,
      dailyLimitCrypto: dailyLimit,
      currency: opportunity.base,
      remainingDailyFiat,
      fiatCurrency,
    };
  }, [
    withdrawalChance,
    budget,
    buyPrice,
    opportunity.buy_quote,
    opportunity.base,
  ]);

  // Calculate futures open levels based on buy volume and leverage
  // Note: Since futures orderbooks are no longer subscribed (per Epic 10),
  // we use bestAsk from book ticker stream as the single price level
  const futuresOpenLevels = useMemo((): PriceLevel[] | undefined => {
    if (!markPrice || !markPrice.bestAsk || markPrice.bestAsk <= 0) {
      return undefined;
    }

    // Calculate volume we need to hedge
    const buyVolume = budget / buyPrice;
    const volumeAfterFee = buyVolume * (1 - (opportunity.buy_fee || 0));

    // Create single level using bestAsk price from book ticker
    // Volume is the full amount we need to hedge (no depth available)
    const openLevels: PriceLevel[] = [
      {
        price: markPrice.bestAsk,
        volume: volumeAfterFee,
      },
    ];

    console.log("✅ Calculated futuresOpenLevels from bestAsk:", {
      bestAsk: markPrice.bestAsk,
      totalVolume: volumeAfterFee,
    });

    return openLevels;
  }, [markPrice, budget, buyPrice, opportunity.buy_fee]);

  // Update time ticker every second
  useEffect(() => {
    if (!open) return;
    const timer = setInterval(() => {
      setTimeTicker((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [open]);

  // Fetch withdrawal chance ONCE when dialog opens (only for Korean exchanges)
  // This is used to check daily withdrawal limits that could cap the budget
  const withdrawalChanceFetchedRef = useRef(false);

  useEffect(() => {
    // Reset the ref when dialog closes
    if (!open) {
      withdrawalChanceFetchedRef.current = false;
      return;
    }

    // Only fetch once per dialog session
    if (withdrawalChanceFetchedRef.current) {
      return;
    }

    // Only fetch for Korean exchanges (Upbit=0, Bithumb=1)
    const buyExchangeIndex = initialOpportunity.buy_exchange;
    if (buyExchangeIndex !== 0 && buyExchangeIndex !== 1) {
      setWithdrawalChance(null);
      setWithdrawalChanceError(null);
      return;
    }

    // Mark as fetched immediately to prevent duplicate calls
    withdrawalChanceFetchedRef.current = true;

    const exchangeName = buyExchangeIndex === 0 ? "upbit" : "bithumb";
    const currency = initialOpportunity.base;
    // For network, use the base currency as the network (most common case)
    const network = currency;

    const fetchWithdrawalChance = async () => {
      setWithdrawalChanceLoading(true);
      setWithdrawalChanceError(null);

      try {
        const response = await getWithdrawalChance(
          traderName,
          exchangeName,
          currency,
          network,
        );
        setWithdrawalChance(response);
        console.log("📊 Withdrawal chance loaded:", response);
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to get withdrawal chance";
        // Don't show error for validation errors about network type - these are expected for some tokens
        // Server returns code: "invalid_network" for these cases
        if (
          message.includes("net_type") ||
          message.includes("invalid_network") ||
          message.includes("not valid for currency")
        ) {
          console.warn(
            "⚠️ Withdrawal chance unavailable (network type issue):",
            currency,
          );
          // Don't set error - just silently skip, daily limit won't be applied
        } else {
          setWithdrawalChanceError(message);
          console.error("❌ Withdrawal chance error:", message);
        }
      } finally {
        setWithdrawalChanceLoading(false);
      }
    };

    fetchWithdrawalChance();
  }, [
    open,
    initialOpportunity.buy_exchange,
    initialOpportunity.base,
    traderName,
  ]);

  // Reset state when dialog opens (but not on every live opportunity update)
  // Uses ref to ensure initialization only happens once per dialog session
  useEffect(() => {
    if (open) {
      // Only initialize once when dialog opens, not on every opportunity update
      if (!initializedForSessionRef.current) {
        initializedForSessionRef.current = true;

        setSelectedStrategy(null);
        setBudget(settings.strategyBudget);
        setLeverage(settings.leverage);
        setProfitTiers(settings.profitTiers);
        setSpotFuturesGapMagnitude(
          (
            settings.hedgeConfig.spotFuturesGapMagnitude ??
            DEFAULT_SPOT_FUTURES_GAP_MAGNITUDE_PERCENT
          ).toString(),
        );

        // Initialize adaptive profit percentage from opportunity spread
        // Calculate fresh spread to avoid stale data
        const buyPrice = opportunity.buy_price || 0;
        const sellPrice = opportunity.sell_price || 0;
        let initialProfitPercentage = 0.05; // Default 5%

        if (buyPrice > 0 && sellPrice > 0) {
          const spreadAmount = sellPrice - buyPrice;
          const currentSpreadPercent = (spreadAmount / buyPrice) * 100; // e.g., 6.0 for 6%
          initialProfitPercentage = currentSpreadPercent / 100; // Convert to decimal: 0.06

          // Validate: ensure it's a reasonable value (between 0.01% and 100%)
          if (
            initialProfitPercentage < 0.0001 ||
            initialProfitPercentage > 1.0
          ) {
            console.warn(
              "Invalid profit percentage calculated:",
              initialProfitPercentage,
              "using default 5%",
            );
            initialProfitPercentage = 0.05;
          }
        }

        console.log("Initializing adaptive profit percentage:", {
          buyPrice,
          sellPrice,
          spreadAmount: sellPrice - buyPrice,
          spreadPercent: ((sellPrice - buyPrice) / buyPrice) * 100,
          initialProfitPercentage,
          displayPercent: (initialProfitPercentage * 100).toFixed(2) + "%",
        });

        setAdaptiveProfitPercentage(initialProfitPercentage);
        setIsFrozen(false);
        setFrozenOpportunity(null);
        setPreviewDialogOpen(false);
        setPreviewJson("");
      }
    } else {
      // Reset the initialization flag when dialog closes
      // So next open will reinitialize properly
      initializedForSessionRef.current = false;
    }
  }, [
    open,
    opportunity,
    settings.strategyBudget,
    settings.leverage,
    settings.profitTiers,
    settings.hedgeConfig.spotFuturesGapMagnitude,
  ]);

  // Handle budget change and persist
  const handleBudgetChange = useCallback(
    (newBudget: number) => {
      setBudget(newBudget);
      saveSettings({ ...settings, strategyBudget: newBudget });
    },
    [settings, saveSettings],
  );

  // Handle leverage change and persist
  const handleLeverageChange = useCallback(
    (newLeverage: number) => {
      setLeverage(newLeverage);
      saveSettings({ ...settings, leverage: newLeverage });
    },
    [settings, saveSettings],
  );

  // Handle profit tiers change and persist
  const handleProfitTiersChange = useCallback(
    (newTiers: ProfitTier[]) => {
      setProfitTiers(newTiers);
      saveSettings({ ...settings, profitTiers: newTiers });
    },
    [settings, saveSettings],
  );

  const handleSpotFuturesGapMagnitudeChange = useCallback(
    (newMagnitudeInput: string) => {
      setSpotFuturesGapMagnitude(newMagnitudeInput);

      const newMagnitudePercent = parseFloat(newMagnitudeInput);
      if (!Number.isFinite(newMagnitudePercent)) {
        return;
      }

      saveSettings({
        ...settings,
        hedgeConfig: {
          ...settings.hedgeConfig,
          spotFuturesGapMagnitude: newMagnitudePercent,
        },
      });
    },
    [settings, saveSettings],
  );

  const spotFuturesGapMagnitudeValue = parseFloat(spotFuturesGapMagnitude);

  // Handle freeze/unfreeze toggle
  const handleToggleFreeze = useCallback(() => {
    if (!isFrozen) {
      // Freezing - capture current opportunity snapshot
      setFrozenOpportunity(opportunity);
      setIsFrozen(true);
    } else {
      // Unfreezing - clear frozen snapshot
      setIsFrozen(false);
      setFrozenOpportunity(null);
    }
  }, [isFrozen, opportunity]);

  const standbyExecutionOptions = useMemo(() => {
    if (
      selectedStrategy !== "StandbyHedgedCross" &&
      selectedStrategy !== "StandbyHedged"
    ) {
      return {};
    }

    return {
      hedgeRebalancing: {
        price_surge_threshold: settings.hedgeConfig.priceSurgeThreshold / 100,
        liquidation_proximity_threshold:
          settings.hedgeConfig.liquidationProximity / 100,
        spot_futures_gap_magnitude_ratio: spotFuturesGapMagnitudeValue / 100,
        entry_funding_rate_threshold:
          settings.hedgeConfig.entryFundingRate === null
            ? null
            : settings.hedgeConfig.entryFundingRate / 100,
        entry_funding_interval_hours:
          settings.hedgeConfig.entryFundingIntervalHours,
        low_funding_rate_threshold: settings.hedgeConfig.lowFundingRate / 100,
        funding_time_window_seconds: settings.hedgeConfig.fundingTimeWindow,
      },
      buyOpenMode:
        settings.buyOpenMode === "Market"
          ? {
              type: "Market" as const,
              max_notional_usdt: settings.marketNotionalUsdt,
            }
          : { type: "Limit" as const },
      replayEnabled: settings.replayEnabled,
      replayDelaySeconds: settings.replayDelaySeconds,
    };
  }, [
    selectedStrategy,
    settings.hedgeConfig.priceSurgeThreshold,
    settings.hedgeConfig.liquidationProximity,
    spotFuturesGapMagnitudeValue,
    settings.hedgeConfig.entryFundingRate,
    settings.hedgeConfig.entryFundingIntervalHours,
    settings.hedgeConfig.lowFundingRate,
    settings.hedgeConfig.fundingTimeWindow,
    settings.buyOpenMode,
    settings.marketNotionalUsdt,
    settings.replayEnabled,
    settings.replayDelaySeconds,
  ]);

  // Handle preview request (show what will be sent)
  const handlePreviewRequest = useCallback(async () => {
    if (!selectedStrategy) return;

    try {
      const response = await executeStrategy({
        opportunity,
        strategyType: selectedStrategy,
        budget,
        leverage,
        profitTiers:
          selectedStrategy === "StandbyHedgedCross" ||
          selectedStrategy === "StandbyHedged"
            ? profitTiers
            : undefined,
        adaptiveProfitPercentage:
          selectedStrategy === "AdaptiveHedgedCross"
            ? adaptiveProfitPercentage
            : undefined,
        futuresOpenLevels,
        ...standbyExecutionOptions,
        previewOnly: true,
        traderName,
      });

      if ("requestJson" in response) {
        setPreviewJson(response.requestJson);
        setPreviewDialogOpen(true);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Failed to generate preview";
      setSnackbarMessage(message);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  }, [
    selectedStrategy,
    opportunity,
    budget,
    leverage,
    profitTiers,
    adaptiveProfitPercentage,
    futuresOpenLevels,
    standbyExecutionOptions,
    traderName,
  ]);

  // Handle strategy execution
  const handleExecuteStrategy = useCallback(async () => {
    if (!selectedStrategy) return;

    setExecuting(true);

    try {
      const response = await executeStrategy({
        opportunity,
        strategyType: selectedStrategy,
        budget,
        leverage,
        profitTiers:
          selectedStrategy === "StandbyHedgedCross" ||
          selectedStrategy === "StandbyHedged"
            ? profitTiers
            : undefined,
        adaptiveProfitPercentage:
          selectedStrategy === "AdaptiveHedgedCross"
            ? adaptiveProfitPercentage
            : undefined,
        futuresOpenLevels,
        ...standbyExecutionOptions,
        traderName,
      });

      if ("strategy_id" in response) {
        setSnackbarMessage(
          t("strategyDialog.successMessage", { id: response.strategy_id }),
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        // Close dialog on success
        setTimeout(() => onClose(), 1000);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message
          : t("strategyDialog.errorFailed");
      setSnackbarMessage(message);
      setSnackbarSeverity(
        message.includes("not yet available") ? "warning" : "error",
      );
      setSnackbarOpen(true);
    } finally {
      setExecuting(false);
    }
  }, [
    selectedStrategy,
    opportunity,
    budget,
    leverage,
    profitTiers,
    adaptiveProfitPercentage,
    futuresOpenLevels,
    standbyExecutionOptions,
    traderName,
    onClose,
    t,
  ]);

  // Handle strategy card click
  const handleStrategyClick = useCallback((strategyType: StrategyType) => {
    // Select the strategy
    setSelectedStrategy(strategyType);
  }, []);

  // Close snackbar
  const handleSnackbarClose = useCallback(() => {
    setSnackbarOpen(false);
  }, []);

  const isHedgeStrategy = selectedStrategy && selectedStrategy !== "Cross";
  const isStandbyHedgedStrategy =
    selectedStrategy === "StandbyHedgedCross" ||
    selectedStrategy === "StandbyHedged";
  const isValidSpotFuturesGapMagnitude = Number.isFinite(
    spotFuturesGapMagnitudeValue,
  );

  // Calculate total volume percentage for profit tiers
  const totalVolumePercentage = useMemo(() => {
    return profitTiers.reduce((sum, tier) => {
      // Treat 0 as "remaining" which dynamically fills to 100%
      if (tier.volume_percentage_to_sell === 0) return sum;
      return sum + tier.volume_percentage_to_sell * 100;
    }, 0);
  }, [profitTiers]);

  // Check if there's a "remaining" tier (volume = 0)
  const hasRemainingTier = profitTiers.some(
    (tier) => tier.volume_percentage_to_sell === 0,
  );

  // Validate that total equals 100% (or has a remaining tier)
  const isValidTierSum =
    hasRemainingTier || Math.abs(totalVolumePercentage - 100) < 0.01;

  // Handle adding a new tier
  const handleAddTier = useCallback(() => {
    const newTiers = [
      ...profitTiers,
      { profit_percentage: 0.15, volume_percentage_to_sell: 0 },
    ];
    handleProfitTiersChange(newTiers);
  }, [profitTiers, handleProfitTiersChange]);

  // Handle removing a tier
  const handleRemoveTier = useCallback(
    (index: number) => {
      if (profitTiers.length > 1) {
        const newTiers = profitTiers.filter((_, i) => i !== index);
        handleProfitTiersChange(newTiers);
      }
    },
    [profitTiers, handleProfitTiersChange],
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1,
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              flex: 1,
              justifyContent: "center",
            }}
          >
            <Avatar
              src={getExchangeLogo(opportunity.buy_exchange)}
              sx={{ width: 28, height: 28 }}
            >
              {buyExchange[0]}
            </Avatar>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: buyQuoteColor,
                background:
                  opportunity.buy_quote === "K" ? buyQuoteColor : undefined,
                backgroundClip:
                  opportunity.buy_quote === "K" ? "text" : undefined,
                WebkitBackgroundClip:
                  opportunity.buy_quote === "K" ? "text" : undefined,
                WebkitTextFillColor:
                  opportunity.buy_quote === "K" ? "transparent" : undefined,
              }}
            >
              {buyQuoteSymbol}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="text.secondary">
              —
            </Typography>
            <Typography variant="h5" fontWeight="bold">
              {opportunity.base}
            </Typography>
            <Typography variant="h6" fontWeight="bold" color="text.secondary">
              —
            </Typography>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 900,
                color: sellQuoteColor,
                background:
                  opportunity.sell_quote === "K" ? sellQuoteColor : undefined,
                backgroundClip:
                  opportunity.sell_quote === "K" ? "text" : undefined,
                WebkitBackgroundClip:
                  opportunity.sell_quote === "K" ? "text" : undefined,
                WebkitTextFillColor:
                  opportunity.sell_quote === "K" ? "transparent" : undefined,
              }}
            >
              {sellQuoteSymbol}
            </Typography>
            <Avatar
              src={getExchangeLogo(opportunity.sell_exchange)}
              sx={{ width: 28, height: 28 }}
            >
              {sellExchange[0]}
            </Avatar>
          </Box>
          {/* Freeze/Unfreeze Button */}
          <IconButton
            aria-label={isFrozen ? "unfreeze" : "freeze"}
            onClick={handleToggleFreeze}
            sx={{
              position: "absolute",
              right: 48,
              top: 8,
              color: isFrozen
                ? "primary.main"
                : (theme) => theme.palette.grey[500],
              backgroundColor: isFrozen
                ? "rgba(25, 118, 210, 0.1)"
                : "transparent",
            }}
          >
            {isFrozen ? <AcUnitIcon /> : <PauseIcon />}
          </IconButton>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Frozen state banner */}
          {isFrozen && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t("strategyDialog.frozenBanner")}
            </Alert>
          )}

          {/* Warning banner if opportunity is no longer available */}
          {!isAvailable && !isFrozen && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t("strategyDialog.unavailableBanner")}
            </Alert>
          )}

          {/* Daily withdrawal limit cap warning */}
          {dailyLimitCap && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2" fontWeight="bold" gutterBottom>
                Daily Withdrawal Limit Cap Applied
              </Typography>
              <Typography variant="body2">
                Original budget:{" "}
                <strong>${dailyLimitCap.originalBudget.toFixed(2)} USDT</strong>
              </Typography>
              <Typography variant="body2">
                Daily limit:{" "}
                <strong>
                  {dailyLimitCap.dailyLimitCrypto} {dailyLimitCap.currency}
                </strong>{" "}
                → <strong>${dailyLimitCap.cappedBudget.toFixed(2)} USDT</strong>
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                Remaining daily allowance:{" "}
                {dailyLimitCap.remainingDailyFiat.toLocaleString()}{" "}
                {dailyLimitCap.fiatCurrency}
              </Typography>
            </Alert>
          )}

          {/* Loading withdrawal chance */}
          {withdrawalChanceLoading && (
            <Alert
              severity="info"
              sx={{ mb: 2 }}
              icon={<CircularProgress size={16} />}
            >
              Checking withdrawal limits...
            </Alert>
          )}

          {/* Withdrawal chance error */}
          {withdrawalChanceError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to check withdrawal limits: {withdrawalChanceError}
            </Alert>
          )}

          {/* Opportunity Details */}
          <Box
            sx={{
              p: 2,
              border: "1px solid rgba(255, 255, 255, 0.12)",
              borderRadius: 1,
              mb: 2,
              backgroundColor: "rgba(0, 0, 0, 0.2)",
            }}
          >
            <ProfitBreakdownDisplay opportunity={opportunity} budget={budget} />

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ mt: 1, display: "block" }}
            >
              {isFrozen
                ? t("strategyDialog.dataFrozen")
                : t("strategyDialog.lastUpdated", {
                    time: formatTimeSinceUpdate(lastUpdateTime),
                  })}
            </Typography>
          </Box>

          {/* Budget Control */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              {t("strategyDialog.budget")}
            </Typography>
            <TextField
              type="number"
              value={budget}
              onChange={(e) => handleBudgetChange(Number(e.target.value))}
              fullWidth
              size="small"
              inputProps={{ min: 5, max: 100000, step: 1 }}
              helperText="Minimum: 5 USDT"
            />
          </Box>

          {/* Leverage Control (for hedge strategies) */}
          {isHedgeStrategy && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t("strategyDialog.leverage", { value: leverage })}
              </Typography>
              <Slider
                value={leverage}
                onChange={(_, value) => handleLeverageChange(value as number)}
                min={1}
                max={50}
                step={1}
                marks={[
                  { value: 1, label: "1x" },
                  { value: 10, label: "10x" },
                  { value: 25, label: "25x" },
                  { value: 50, label: "50x" },
                ]}
                valueLabelDisplay="auto"
              />
            </Box>
          )}

          {/* Profit Percentage (for AdaptiveHedgedCross) */}
          {selectedStrategy === "AdaptiveHedgedCross" && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Target Profit Percentage
              </Typography>
              <TextField
                type="number"
                value={(adaptiveProfitPercentage * 100).toFixed(2)}
                onChange={(e) =>
                  setAdaptiveProfitPercentage(Number(e.target.value) / 100)
                }
                fullWidth
                size="small"
                inputProps={{ min: 0.01, max: 100, step: 0.01 }}
                helperText={`Default from opportunity spread: ${spreadPercent.toFixed(2)}%. Stored as decimal: ${adaptiveProfitPercentage.toFixed(4)}`}
                InputProps={{
                  endAdornment: "%",
                }}
              />
            </Box>
          )}

          {isStandbyHedgedStrategy && (
            <Box sx={{ mb: 3 }}>
              <TextField
                label={t("strategyDialog.spotFuturesGapMagnitude")}
                type="text"
                value={spotFuturesGapMagnitude}
                onChange={(e) =>
                  handleSpotFuturesGapMagnitudeChange(e.target.value)
                }
                fullWidth
                size="small"
                error={!isValidSpotFuturesGapMagnitude}
                inputProps={{ inputMode: "decimal" }}
                helperText={
                  isValidSpotFuturesGapMagnitude
                    ? t("strategyDialog.spotFuturesGapMagnitudeHelp")
                    : t("strategyDialog.spotFuturesGapMagnitudeInvalid")
                }
                InputProps={{
                  endAdornment: "%",
                }}
              />
            </Box>
          )}

          {/* Profit Tiers (for StandbyHedgedCross and StandbyHedged - AdaptiveHedgedCross uses single profit percentage above) */}
          {(selectedStrategy === "StandbyHedgedCross" ||
            selectedStrategy === "StandbyHedged") && (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle2">
                  {t("strategyDialog.profitTiers")}
                </Typography>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={handleAddTier}
                >
                  {t("strategyDialog.addTier")}
                </Button>
              </Box>

              {profitTiers.map((tier, index) => (
                <Box
                  key={index}
                  sx={{ display: "flex", gap: 1, mb: 1, alignItems: "center" }}
                >
                  <TextField
                    label={t("strategyDialog.profitPercent")}
                    type="number"
                    size="small"
                    sx={{ width: "50%" }}
                    value={(tier.profit_percentage * 100).toFixed(0)}
                    onChange={(e) => {
                      const newTiers = profitTiers.map((t, i) =>
                        i === index
                          ? {
                              ...t,
                              profit_percentage: Number(e.target.value) / 100,
                            }
                          : { ...t },
                      );
                      handleProfitTiersChange(newTiers);
                    }}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                  />
                  <TextField
                    label={t("strategyDialog.sellPercent")}
                    type="number"
                    size="small"
                    sx={{ width: "50%" }}
                    value={(tier.volume_percentage_to_sell * 100).toFixed(0)}
                    onChange={(e) => {
                      const newTiers = profitTiers.map((t, i) =>
                        i === index
                          ? {
                              ...t,
                              volume_percentage_to_sell:
                                Number(e.target.value) / 100,
                            }
                          : { ...t },
                      );
                      handleProfitTiersChange(newTiers);
                    }}
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    helperText={
                      tier.volume_percentage_to_sell === 0
                        ? t("strategyDialog.remaining")
                        : ""
                    }
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveTier(index)}
                    disabled={profitTiers.length === 1}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ))}

              {/* Validation message */}
              {!isValidTierSum && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  {t("strategyDialog.totalVolume", {
                    total: totalVolumePercentage.toFixed(0),
                  })}
                </Alert>
              )}
              {isValidTierSum && !hasRemainingTier && (
                <Typography
                  variant="caption"
                  color="success.main"
                  sx={{ display: "block", mt: 1 }}
                >
                  {t("strategyDialog.totalVolumeValid", {
                    total: totalVolumePercentage.toFixed(0),
                  })}
                </Typography>
              )}
              {hasRemainingTier && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 1 }}
                >
                  {t("strategyDialog.remainingTier")}
                </Typography>
              )}
            </Box>
          )}

          {/* Strategy Selection - Compact horizontal layout */}
          <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
            {t("strategyDialog.selectStrategy")}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            sx={{ mb: 2, flexWrap: "wrap", gap: 1 }}
          >
            {availableStrategies.map((strategyType) => {
              const name = t(`strategyDialog.strategies.${strategyType}`);
              const isSelected = selectedStrategy === strategyType;
              const isAvailableStrategy =
                availableStrategies.includes(strategyType);

              return (
                <Button
                  key={strategyType}
                  variant={isSelected ? "contained" : "outlined"}
                  size="small"
                  disabled={!isAvailableStrategy}
                  onClick={() =>
                    isAvailableStrategy && handleStrategyClick(strategyType)
                  }
                  sx={{
                    minWidth: "auto",
                    px: 1.5,
                    py: 0.5,
                    fontSize: "0.75rem",
                    borderColor: isSelected
                      ? "primary.main"
                      : "rgba(255, 255, 255, 0.23)",
                    "&:hover": {
                      borderColor: "primary.main",
                    },
                  }}
                >
                  {name}
                  {isSelected && executing && (
                    <CircularProgress
                      size={12}
                      sx={{ ml: 1 }}
                      color="inherit"
                    />
                  )}
                </Button>
              );
            })}
          </Stack>

          {/* Orderbook Cards - Show when strategy is selected */}
          {selectedStrategy && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Live Orderbooks
              </Typography>
              <Grid container spacing={1.5}>
                {/* Buy Orderbook (Asks - where we buy) */}
                <Grid size={{ xs: 12, sm: isHedgeStrategy ? 4 : 6 }}>
                  <OrderbookCard
                    title={`Buy @ ${buyExchange}`}
                    exchangeIndex={opportunity.buy_exchange}
                    levels={sortedBuyAsks}
                    loading={buyOrderbook.loading && sortedBuyAsks.length === 0}
                    error={buyOrderbook.error}
                    quoteCurrency={buyQuote}
                    avgPrice={buyCoverage.avgPrice}
                    highlightCount={buyCoverage.highlightCount}
                    isBid={false}
                    quoteToUsdtRate={
                      opportunity.buy_quote === "K"
                        ? krwToUsdtRate
                        : opportunity.buy_quote === "B"
                          ? btcUsdtRate
                          : null
                    }
                  />
                </Grid>

                {/* Sell Orderbook (Bids - where we sell) */}
                <Grid size={{ xs: 12, sm: isHedgeStrategy ? 4 : 6 }}>
                  <OrderbookCard
                    title={`Sell @ ${sellExchange}`}
                    exchangeIndex={opportunity.sell_exchange}
                    levels={sortedSellBids}
                    loading={
                      sellOrderbook.loading && sortedSellBids.length === 0
                    }
                    error={sellOrderbook.error}
                    quoteCurrency={sellQuote}
                    avgPrice={sellCoverage.avgPrice}
                    highlightCount={sellCoverage.highlightCount}
                    isBid={true}
                    quoteToUsdtRate={
                      opportunity.sell_quote === "K"
                        ? krwToUsdtRate
                        : opportunity.sell_quote === "B"
                          ? btcUsdtRate
                          : null
                    }
                  />
                </Grid>

                {/* Futures Orderbook (Bids - where we hedge) - Only for hedge strategies */}
                {isHedgeStrategy && (
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <OrderbookCard
                      title="Hedge @ Binance Futures"
                      exchangeIndex={3}
                      levels={sortedFuturesBids}
                      loading={
                        futuresOrderbook.loading &&
                        sortedFuturesBids.length === 0
                      }
                      error={futuresOrderbook.error}
                      quoteCurrency="USDT"
                      avgPrice={futuresCoverage.avgPrice}
                      highlightCount={futuresCoverage.highlightCount}
                      isBid={true}
                      quoteToUsdtRate={null}
                    />
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            {t("strategyDialog.cancel")}
          </Button>
          <Button
            onClick={handlePreviewRequest}
            variant="outlined"
            color="info"
            disabled={
              !selectedStrategy ||
              executing ||
              (isStandbyHedgedStrategy && !isValidSpotFuturesGapMagnitude) ||
              ((selectedStrategy === "StandbyHedgedCross" ||
                selectedStrategy === "StandbyHedged") &&
                !isValidTierSum)
            }
          >
            Preview Request
          </Button>
          <Button
            onClick={handleExecuteStrategy}
            variant="contained"
            color="primary"
            disabled={
              !selectedStrategy ||
              executing ||
              (isStandbyHedgedStrategy && !isValidSpotFuturesGapMagnitude) ||
              ((selectedStrategy === "StandbyHedgedCross" ||
                selectedStrategy === "StandbyHedged") &&
                !isValidTierSum)
            }
            startIcon={executing ? <CircularProgress size={20} /> : undefined}
          >
            {executing
              ? t("strategyDialog.executing")
              : t("strategyDialog.execute")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={snackbarSeverity === "success" ? 5000 : null}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Debug Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Request Preview (Debug)
          <IconButton
            aria-label="close"
            onClick={() => setPreviewDialogOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            This shows exactly what will be sent to the backend API. This is for
            debugging purposes only.
          </Alert>
          <Paper
            sx={{
              p: 2,
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              maxHeight: "60vh",
              overflow: "auto",
            }}
          >
            <pre
              style={{
                margin: 0,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {previewJson}
            </pre>
          </Paper>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewDialogOpen(false)}
            variant="outlined"
          >
            Close
          </Button>
          <Button
            onClick={() => {
              setPreviewDialogOpen(false);
              handleExecuteStrategy();
            }}
            variant="contained"
            color="primary"
            disabled={executing}
          >
            Confirm & Execute
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
